import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, FARM_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { grainBinLabel } from "@/lib/grainBinLabel";
import type {
  Field,
  FieldCrop,
  FieldCropApplication,
  FieldApplicationType,
  FieldCropHarvest,
  FieldHarvestUnit,
  GrainBin,
  GrainContract,
  Guid,
} from "@/api/types";
import { DialogShell } from "@/components/DialogShell";
import "./Fields.css";
import "./FieldsExtra.css";

/**
 * ef-045-3 — the owner's Fields screen, `/farm/fields`: a crop-year filter, the field
 * list, add-field, and per-field crop-year assignment (crop + acres — D3's own
 * double-crop case, more than one crop the same field the same year, is representable
 * here). Calls ef-045-2's routes (`FieldEndpoints.cs`/`FieldContracts.cs`) directly —
 * `api/client.ts`'s own `// ---- ef-045-3: fields ----` section. Per ADR 0009's "one page
 * template" — `PageHeader` (title + the one primary action, "New field") → the Farm area
 * tab strip → content in a card.
 *
 * NO SEPARATE DIALOG/ROW COMPONENT FILE — unlike `Livestock.tsx`'s own
 * `AnimalDialog`/`AnimalDetail`, this job's own file scope names only `Fields.tsx` +
 * `Fields.css`, so the "New field" dialog and the per-row assign-crop form both live
 * here as local, unexported components.
 *
 * NO ARCHIVE/RESTORE — `FieldEndpoints.cs` ships none this wave (0059's `archived_at`
 * has no route that sets it), matching `fields.spec.ts`'s own header comment. Flagged in
 * this job's own return contract rather than invented here.
 *
 * ONE CROP YEAR AT A TIME — the "Crop year" picker is this screen's own filter, not a
 * server concept: `GET .../field-crops?year=` scopes every field's assignments to it in
 * one request, grouped client-side by `fieldId` (`cropsByField` below). A field itself
 * carries no year — only its crop assignments do.
 */

type FieldsStatus = "loading" | "ready" | "error";

function currentYear(): number {
  return new Date().getFullYear();
}

/** Groups crop-year assignments by their own field, for one render pass per row —
 *  never re-derived elsewhere (same "one place decides" convention `Livestock.tsx`'s own
 *  `groupByHerd` documents for itself). */
function groupByField(crops: FieldCrop[]): Map<Guid, FieldCrop[]> {
  const map = new Map<Guid, FieldCrop[]>();
  for (const crop of crops) {
    const list = map.get(crop.fieldId);
    if (list) list.push(crop);
    else map.set(crop.fieldId, [crop]);
  }
  return map;
}

export function Fields() {
  // ef-047-1 — replaces this screen's own `entities[0]` guess with the shared, persisted
  // switcher (`EntitySwitcher.tsx`, mounted in `AppShell.tsx`'s Farm-area entity bar).
  // `entityStatus` gates `load()` below the same way this screen always waited for its
  // own `getEntities()` call to resolve before fetching fields — and `entityId` in the
  // effect's own deps is what makes switching entities live-update this screen with no
  // reload/navigation in between.
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [fields, setFields] = useState<Field[] | null>(null);
  const [crops, setCrops] = useState<FieldCrop[] | null>(null);
  const [year, setYear] = useState<number>(currentYear);
  const [status, setStatus] = useState<FieldsStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  /** Re-reads the whole screen — fields for the entity, crop-year assignments for
   *  `forYear` — same "wholesale reload after every write, never patch arrays in
   *  place" convention `useLivestock.ts` documents for itself, so a row's own derived
   *  crop list never drifts from what the server actually holds. */
  const load = useCallback(async (forYear: number, eid: Guid | null): Promise<void> => {
    setStatus("loading");
    setError(null);
    try {
      const [fieldList, cropList] = await Promise.all([
        demoApi.getFields(eid ?? undefined),
        demoApi.getFieldCrops({ year: forYear }),
      ]);
      setFields(fieldList);
      setCrops(cropList);
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load fields."));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (entityStatus === "loading") return;
    void load(year, entityId);
  }, [load, year, entityId, entityStatus]);

  const sortedFields = useMemo(
    () => [...(fields ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [fields],
  );
  const cropsByField = useMemo(() => groupByField(crops ?? []), [crops]);

  /** ef-045-4 — the Applications section below lists every crop-year assignment flat
   *  (not grouped by field, unlike `cropsByField` above), so each entry needs its own
   *  field name alongside it. Purely additive: `fields`/`crops` are the same state
   *  slice-3's own `load()` already populates, never re-fetched here. */
  const fieldNameById = useMemo(() => {
    const map = new Map<Guid, string>();
    for (const f of fields ?? []) map.set(f.id, f.name);
    return map;
  }, [fields]);

  const assignCrop = useCallback(
    async (fieldId: Guid, crop: string, acres: number): Promise<void> => {
      await demoApi.createFieldCrop({ fieldId, year, crop, acres });
      await load(year, entityId);
    },
    [load, year, entityId],
  );

  const yearId = useId();

  return (
    <main className="fields-page">
      <PageHeader
        title="Fields"
        subtitle="Fields and the crops assigned to them, by year."
        action={
          <button type="button" className="btn btn--primary" onClick={() => setCreateOpen(true)}>
            New field
          </button>
        }
      />

      <AreaTabs area="/farm" tabs={FARM_TABS} label="Farm" />

      <section
        className="fields-page__card"
        aria-labelledby="fields-heading"
        data-state={status === "ready" && sortedFields.length === 0 ? "empty" : status}
      >
        <div className="fields-page__card-head">
          <h2 id="fields-heading" className="fields-page__card-title">
            Fields
          </h2>
          <span className="fields-page__year">
            <label htmlFor={yearId}>Crop year</label>
            <input
              id={yearId}
              type="number"
              min={2000}
              max={2100}
              value={year}
              onChange={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next)) setYear(next);
              }}
            />
          </span>
        </div>

        <Why>
          A field can carry more than one crop the same year — a cover crop after
          harvest, or two cash crops split across the same ground. Each assignment keeps
          its own planted acres, separate from the field's own measured size.
        </Why>

        {status === "loading" && <StateMessage tone="loading" title="Loading fields…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load fields." detail={error ?? undefined} />
        )}

        {status === "ready" && sortedFields.length === 0 && (
          <StateMessage
            tone="empty"
            title="No fields yet."
            detail="Add your first field and assign a crop for the year."
          />
        )}

        {status === "ready" && sortedFields.length > 0 && (
          <ul className="fields-page__list" aria-label="Fields">
            {sortedFields.map((field) => (
              <FieldRow
                key={field.id}
                field={field}
                crops={cropsByField.get(field.id) ?? []}
                year={year}
                onAssignCrop={assignCrop}
              />
            ))}
          </ul>
        )}
      </section>

      {/*
        ef-045-4 — the applications log, appended below slice 3's own Fields card.
        Additive only: reads `crops`/`fields`/`status`/`error`/`fieldNameById` from THIS
        component's own state above, never touching `FieldRow`, `NewFieldDialog`, or the
        crop-year filter. One flat list of every crop-year assignment for the selected
        `year` (same scope `crops` already has) rather than nested inside each `FieldRow`,
        so slice 3's own row markup stays byte-for-byte what it was before this job.
      */}
      <section
        className="fields-page__card"
        aria-labelledby="applications-heading"
        data-state={status === "ready" && (crops ?? []).length === 0 ? "empty" : status}
      >
        <div className="fields-page__card-head">
          <h2 id="applications-heading" className="fields-page__card-title">
            Applications
          </h2>
        </div>

        <Why>
          Every chemical, fertilizer, or seed application logs against one field's crop
          for the year — never against the field itself. A costed application is a record
          kept here, not an entry on the books; it never posts to the ledger.
        </Why>

        {status === "loading" && <StateMessage tone="loading" title="Loading applications…" />}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load applications."
            detail={error ?? undefined}
          />
        )}

        {status === "ready" && (crops ?? []).length === 0 && (
          <StateMessage
            tone="empty"
            title="No crops assigned yet."
            detail="Assign a crop above, then log applications against it here."
          />
        )}

        {status === "ready" && (crops ?? []).length > 0 && (
          <ul className="fields-page__application-groups" aria-label="Applications by field crop">
            {(crops ?? []).map((c) => (
              <FieldCropApplications
                key={c.id}
                fieldCropId={c.id}
                fieldName={fieldNameById.get(c.fieldId) ?? "Field"}
                crop={c.crop}
                acres={c.acres}
                entityId={entityId}
              />
            ))}
          </ul>
        )}
      </section>

      {createOpen && (
        <NewFieldDialog
          entityId={entityId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => void load(year, entityId)}
        />
      )}
    </main>
  );
}

function FieldRow({
  field,
  crops,
  year,
  onAssignCrop,
}: {
  field: Field;
  crops: FieldCrop[];
  year: number;
  onAssignCrop: (fieldId: Guid, crop: string, acres: number) => Promise<void>;
}) {
  const [crop, setCrop] = useState("");
  const [acres, setAcres] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cropId = useId();
  const acresId = useId();

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    /* @ui-check: client-side ONLY — the server's own checks (FieldEndpoints.cs's
       ValidateCrop/ValidateCropAcres) still run after this and remain the actual
       contract enforcement. This guard exists so a farmer sees the same reason
       immediately, without a round trip. */
    const trimmedCrop = crop.trim();
    const acresValue = Number(acres);
    if (trimmedCrop === "") {
      setError("Crop is required.");
      return;
    }
    if (!Number.isFinite(acresValue) || acresValue <= 0) {
      setError("Acres must be greater than zero.");
      return;
    }

    setSubmitting(true);
    try {
      await onAssignCrop(field.id, trimmedCrop, acresValue);
      setCrop("");
      setAcres("");
    } catch (err) {
      // A 409 means this exact (field, year, crop) is already recorded — refused,
      // surfaced INLINE on this row's own form, never a dialog (FieldEndpoints.cs's own
      // CropYearConflict; fields.spec.ts's own "no dialog" assertion for this case).
      setError(problemMessage(err, "Could not assign this crop."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="fields-page__row" data-field-id={field.id}>
      <span className="fields-page__meta">
        <span className="fields-page__tag">{field.name}</span>
        <span className="fields-page__sub">{field.acres != null ? `${field.acres} ac total` : "—"}</span>
      </span>

      <div className="fields-page__crops">
        {crops.length === 0 ? (
          <p className="fields-page__sub">No crop assigned for {year}.</p>
        ) : (
          <ul className="fields-page__crop-list" aria-label={`${field.name} crops, ${year}`}>
            {crops.map((c) => (
              <li key={c.id} className="fields-page__crop-line">
                {c.crop} — {c.acres.toFixed(2)} ac
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="fields-page__crop-form" onSubmit={(e) => void submit(e)} noValidate>
        <span className="fields-page__field">
          <label htmlFor={cropId}>Crop</label>
          <input
            id={cropId}
            type="text"
            maxLength={80}
            value={crop}
            onChange={(e) => setCrop(e.target.value)}
          />
        </span>
        <span className="fields-page__field">
          <label htmlFor={acresId}>Acres</label>
          <input
            id={acresId}
            type="number"
            min="0.01"
            step="0.01"
            value={acres}
            onChange={(e) => setAcres(e.target.value)}
          />
        </span>
        {error && (
          <p className="fields-page__field-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn--primary fields-page__btn" disabled={submitting}>
          {submitting ? "…" : "Add crop"}
        </button>
      </form>
    </li>
  );
}

/**
 * The "New field" dialog Fields.tsx's own header action opens. Same focus-trap +
 * Escape-to-close shape as `AnimalDialog.tsx`, inlined here rather than split into its
 * own component file — this job's own file scope names only `Fields.tsx`/`Fields.css`.
 */
function NewFieldDialog({
  entityId,
  onClose,
  onCreated,
}: {
  entityId: Guid | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [acres, setAcres] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const nameId = useId();
  const acresId = useId();
  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFieldErrors(null);
    setFormError(null);

    /* @ui-check: client-side ONLY — FieldEndpoints.cs's own ValidateName/
       ValidateFieldAcres still run server-side and remain the real contract. This guard
       exists so a blank name never leaves the browser (fields.spec.ts's own "creates
       nothing"). */
    const trimmedName = name.trim();
    if (trimmedName === "") {
      setFieldErrors({ name: ["Name is required."] });
      return;
    }
    const acresValue = acres.trim() === "" ? undefined : Number(acres);
    if (acresValue !== undefined && (!Number.isFinite(acresValue) || acresValue <= 0)) {
      setFieldErrors({ acres: ["Must be greater than zero."] });
      return;
    }
    if (entityId === null) {
      setFormError("No farm entity found for this tenant yet.");
      return;
    }

    setSaving(true);
    try {
      await demoApi.createField({ entityId, name: trimmedName, acres: acresValue });
      onCreated();
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not create this field."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="New field"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : "Add field"}
          </button>
        </>
      }
    >
      <form id={formId} className="fields-page__dialog-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="fields-page__field">
          <label htmlFor={nameId}>Name</label>
          <input
            id={nameId}
            type="text"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={fieldErrors?.name ? true : undefined}
          />
          {fieldErrors?.name && (
            <p className="fields-page__field-error" role="alert">
              {fieldErrors.name[0]}
            </p>
          )}
        </span>

        <span className="fields-page__field">
          <label htmlFor={acresId}>Acres (optional)</label>
          <input
            id={acresId}
            type="number"
            min="0.01"
            step="0.01"
            value={acres}
            onChange={(e) => setAcres(e.target.value)}
            aria-invalid={fieldErrors?.acres ? true : undefined}
          />
          {fieldErrors?.acres && (
            <p className="fields-page__field-error" role="alert">
              {fieldErrors.acres[0]}
            </p>
          )}
        </span>

        {formError && !fieldErrors?.name && !fieldErrors?.acres && (
          <p className="fields-page__dialog-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------------
// ef-045-4 — applications log. APPENDED below slice 3's own `NewFieldDialog`; nothing
// above this line changes. Calls `FieldApplicationEndpoints.cs`/`FieldApplicationContracts.cs`
// via `api/client.ts`'s own `// ---- ef-045-4: applications ----` section. Add-only —
// there is no PATCH/DELETE route this wave (`FieldApplicationEndpoints.cs`'s own header),
// so this file has no edit/remove affordance either, same "flag it, don't invent it"
// posture `Fields()`'s own header takes for the missing archive route.
// ---------------------------------------------------------------------------------

/** "fertilizer" -> "Fertilizer" for display — the wire value stays lowercase
 *  (`FieldApplicationEndpoints.AllowedApplicationTypes` is case-sensitive on purpose). */
function capitalize(value: string): string {
  return value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);
}

/**
 * One field-crop's own applications: its own list (newest first, straight off the wire —
 * `FieldApplicationRepository.GetApplicationsAsync`'s own `ORDER BY`, never re-sorted
 * here) plus its own add form. Fetches and owns its own state rather than folding into
 * `Fields()`'s own `load()` — applications are not part of slice 3's own screen-wide
 * reload, and a field-crop can be added/removed from the page (a different crop year
 * picked) far more often than its own applications change.
 *
 * Renders as the `<li data-field-crop-id>` `fields-applications.spec.ts` locates every
 * fixture by (`ef-045-3-contract.md`'s own Rules, restated in this file's own job brief).
 *
 * ef-045-5 — ALSO owns the Production line (harvest entry + the crop sale it posts),
 * appended below its own group head and above the applications list. Same `<li
 * data-field-crop-id>` as the applications group — `fields-harvest.spec.ts`'s own
 * `cropRow` locates by the SAME attribute `fields-applications.spec.ts` does, so a second
 * element carrying it would make that locator ambiguous. `acres` (the field-crop's own
 * planted acres, already known from `Fields()`'s own `crops` state) is the only new prop
 * this slice adds — yield is computed HERE, client-side, never returned by the server
 * (D6, `FieldCropHarvest`'s own remarks in api/types.ts).
 */
function FieldCropApplications({
  fieldCropId,
  fieldName,
  crop,
  acres,
  entityId,
}: {
  fieldCropId: Guid;
  fieldName: string;
  crop: string;
  acres: number;
  /** ef-046-1c — the harvest form's own bin picker lists bins for THIS entity; null
   *  while `Fields()`'s own entity lookup is still in flight (same "not loaded yet"
   *  posture `entityId` already carries one level up). */
  entityId: Guid | null;
}) {
  const [applications, setApplications] = useState<FieldCropApplication[] | null>(null);
  const [status, setStatus] = useState<FieldsStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setStatus("loading");
    setError(null);
    try {
      const list = await demoApi.getFieldApplications(fieldCropId);
      setApplications(list);
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load applications."));
      setStatus("error");
    }
  }, [fieldCropId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** ef-045-5 — the field-crop's own harvest history, newest `harvestedOn` first
   *  (`getFieldHarvests`'s own remarks). `latestHarvest` (`[0]`) drives the shown
   *  yield — re-fetched after every successful POST rather than trusted from that POST's
   *  own response, so an out-of-order (earlier-dated) harvest never shows a stale "most
   *  recent" yield. */
  const [harvests, setHarvests] = useState<FieldCropHarvest[] | null>(null);
  const [harvestStatus, setHarvestStatus] = useState<FieldsStatus>("loading");
  const [harvestError, setHarvestError] = useState<string | null>(null);

  const loadHarvests = useCallback(async (): Promise<void> => {
    setHarvestStatus("loading");
    setHarvestError(null);
    try {
      const list = await demoApi.getFieldHarvests(fieldCropId);
      setHarvests(list);
      setHarvestStatus("ready");
    } catch (err) {
      setHarvestError(problemMessage(err, "Could not load the harvest."));
      setHarvestStatus("error");
    }
  }, [fieldCropId]);

  useEffect(() => {
    void loadHarvests();
  }, [loadHarvests]);

  const latestHarvest = harvests?.[0] ?? null;
  const yieldPerAcre = latestHarvest ? Math.round((latestHarvest.qty / acres) * 100) / 100 : null;

  return (
    <li className="fields-page__application-group" data-field-crop-id={fieldCropId}>
      <div className="fields-page__application-group-head">
        <span className="fields-page__tag">{fieldName}</span>
        <span className="fields-page__sub">{crop}</span>
      </div>

      <div className="fields-page__production">
        <div className="fields-page__production-head">
          <span className="fields-page__production-title">Production</span>
          {yieldPerAcre != null && latestHarvest && (
            <span className="fields-page__production-yield">
              {yieldPerAcre.toFixed(2)} {latestHarvest.unit}/ac
            </span>
          )}
        </div>

        {harvestStatus === "loading" && <p className="fields-page__sub">Loading harvest…</p>}

        {harvestStatus === "error" && (
          <p className="fields-page__field-error" role="alert">
            {harvestError}
          </p>
        )}

        {harvestStatus === "ready" && !latestHarvest && (
          <p className="fields-page__sub">No harvest recorded yet.</p>
        )}

        {/* ef-057-1a — the DELIVERED branch, checked FIRST: a delivered harvest has
            `entryId == null` and would otherwise read "Stored". */}
        {harvestStatus === "ready" && latestHarvest && latestHarvest.contractId != null && (
          <p className="fields-page__sub">
            Delivered {latestHarvest.qty} {latestHarvest.unit} to contract on {latestHarvest.harvestedOn} for{" "}
            <span className="fields-page__money">${(latestHarvest.deliveryAmount ?? 0).toFixed(2)}</span>
          </p>
        )}

        {harvestStatus === "ready" && latestHarvest && latestHarvest.contractId == null && latestHarvest.entryId != null && (
          <p className="fields-page__sub">
            Sold {latestHarvest.qty} {latestHarvest.unit} on {latestHarvest.harvestedOn} for{" "}
            <span className="fields-page__money">${(latestHarvest.entryAmount ?? 0).toFixed(2)}</span>
          </p>
        )}

        {/* ef-046-1c — the STORED branch (`binId` set, no `entryId`/sale amount at all,
            D4/D8's own negative control). */}
        {harvestStatus === "ready" && latestHarvest && latestHarvest.contractId == null && latestHarvest.entryId == null && (
          <p className="fields-page__sub">
            Stored {latestHarvest.qty} {latestHarvest.unit} on {latestHarvest.harvestedOn}.
          </p>
        )}

        <HarvestForm
          fieldCropId={fieldCropId}
          entityId={entityId}
          crop={crop}
          onRecorded={() => void loadHarvests()}
        />
      </div>

      {status === "loading" && <p className="fields-page__sub">Loading…</p>}

      {status === "error" && (
        <p className="fields-page__field-error" role="alert">
          {error}
        </p>
      )}

      {status === "ready" &&
        (applications === null || applications.length === 0 ? (
          <p className="fields-page__sub">No applications logged yet.</p>
        ) : (
          <ul className="fields-page__application-list" aria-label={`${crop} applications`}>
            {applications.map((a) => (
              <li key={a.id} className="fields-page__application-line">
                <span className="fields-page__application-type">{capitalize(a.applicationType)}</span>
                <span>{a.product}</span>
                {a.rate != null && (
                  <span>
                    {a.rate}
                    {a.rateUnit ? ` ${a.rateUnit}` : ""}
                  </span>
                )}
                <span>{a.appliedOn}</span>
                {a.cost != null && (
                  <span className="fields-page__money">${a.cost.toFixed(2)}</span>
                )}
              </li>
            ))}
          </ul>
        ))}

      <ApplicationForm fieldCropId={fieldCropId} onLogged={() => void load()} />
    </li>
  );
}

/**
 * The per-field-crop add-application form — one `Log application` action, no
 * edit/remove (see this section's own header comment). Mirrors `FieldRow`'s own
 * `submit`/client-guard shape one section up: a client-side check for the exact case
 * `fields-applications.spec.ts`'s own AC3 exercises (blank product), leaving every other
 * rule to the real contract in `FieldApplicationEndpoints.cs`.
 */
const APPLICATION_TYPE_OPTIONS: { value: FieldApplicationType; label: string }[] = [
  { value: "chemical", label: "Chemical" },
  { value: "fertilizer", label: "Fertilizer" },
  { value: "seed", label: "Seed" },
];

function ApplicationForm({
  fieldCropId,
  onLogged,
}: {
  fieldCropId: Guid;
  onLogged: () => void;
}) {
  // Nullable, NOT defaulted to "fertilizer" — `fields-applications.spec.ts`'s own AC1
  // locates the freshly-logged line by `row.getByText(/fertilizer/i)` scoped to this same
  // `[data-field-crop-id]` row the picker lives in. A value that survives a successful
  // submit (whether a native `<select>`'s own always-in-DOM `<option>`, or this trigger's
  // own displayed label) leaves TWO matches for that one query — the new list line AND
  // this form still showing "Fertilizer" — a strict-mode violation, not a flake. Clearing
  // back to no selection on every successful log (and requiring an explicit pick every
  // time, `@ui-check` below) is what keeps the row's accessible text unambiguous.
  const [applicationType, setApplicationType] = useState<FieldApplicationType | null>(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [product, setProduct] = useState("");
  const [rate, setRate] = useState("");
  const [unit, setUnit] = useState("");
  const [appliedOn, setAppliedOn] = useState("");
  const [cost, setCost] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeId = useId();
  const typeListboxId = useId();
  const typeRootRef = useRef<HTMLSpanElement>(null);
  const typeTriggerRef = useRef<HTMLDivElement>(null);
  const productId = useId();
  const rateId = useId();
  const unitId = useId();
  const appliedOnId = useId();
  const costId = useId();

  // Outside-click close — same mechanics as `RowActions`' own disclosure (this form's
  // picker isn't a row of one-shot commands, so it isn't `RowActions` itself, but the
  // "closed unless open" popup behaviour it exists to give a trigger is exactly what
  // keeps this picker's own `role="option"` rows OUT of the DOM (and out of any
  // `getByText` match) whenever the picker isn't open).
  useEffect(() => {
    if (!typeOpen) return;
    function onDocPointerDown(e: MouseEvent): void {
      if (!typeRootRef.current?.contains(e.target as Node)) setTypeOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
  }, [typeOpen]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    /* @ui-check: client-side ONLY — FieldApplicationEndpoints.cs's own
       ValidateProduct/ValidateApplicationType/appliedOn-required/ValidateRate/
       ValidateUnit/ValidateCost checks still run server-side and remain the actual
       contract. Same "surface the reason immediately" posture FieldRow's own submit
       and NewFieldDialog's own handleSubmit take above. */
    const selectedType = applicationType;
    if (selectedType === null) {
      setError("Select an application type.");
      return;
    }
    const trimmedProduct = product.trim();
    if (trimmedProduct === "") {
      setError("Product is required.");
      return;
    }
    if (appliedOn === "") {
      setError("Applied on is required.");
      return;
    }
    const rateValue = rate.trim() === "" ? undefined : Number(rate);
    if (rateValue !== undefined && (!Number.isFinite(rateValue) || rateValue <= 0)) {
      setError("Rate must be greater than zero.");
      return;
    }
    const costValue = cost.trim() === "" ? undefined : Number(cost);
    if (costValue !== undefined && (!Number.isFinite(costValue) || costValue <= 0)) {
      setError("Cost must be greater than zero.");
      return;
    }

    setSubmitting(true);
    try {
      await demoApi.createFieldApplication(fieldCropId, {
        applicationType: selectedType,
        product: trimmedProduct,
        rate: rateValue,
        unit: unit.trim() === "" ? undefined : unit.trim(),
        appliedOn,
        cost: costValue,
      });
      setApplicationType(null);
      setProduct("");
      setRate("");
      setUnit("");
      setAppliedOn("");
      setCost("");
      onLogged();
    } catch (err) {
      setError(problemMessage(err, "Could not log this application."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="fields-page__application-form" onSubmit={(e) => void submit(e)} noValidate>
      <span className="fields-page__field" ref={typeRootRef}>
        <label id={typeId}>Application type</label>
        {/* NOT a native `<select>` — see this state's own remarks above. A closed
            native select's `<option>`s have no visibility gate at all (they're always in
            the DOM, open or not), so a `Fertilizer` option sitting next to a `Fertilizer`
            list line is a permanent strict-mode collision no reset can fix. This picker's
            own `role="option"` rows exist ONLY while `typeOpen`, same disclosure shape
            `RowActions` already uses elsewhere in this app for "closed unless open". */}
        <div
          ref={typeTriggerRef}
          role="combobox"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={typeOpen}
          aria-controls={typeOpen ? typeListboxId : undefined}
          aria-labelledby={typeId}
          className="fields-page__type-trigger"
          onClick={() => setTypeOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
              e.preventDefault();
              setTypeOpen(true);
            } else if (e.key === "Escape" && typeOpen) {
              e.preventDefault();
              e.stopPropagation();
              setTypeOpen(false);
            }
          }}
        >
          {APPLICATION_TYPE_OPTIONS.find((o) => o.value === applicationType)?.label ?? "Select type…"}
        </div>
        {typeOpen && (
          <ul id={typeListboxId} role="listbox" aria-labelledby={typeId} className="fields-page__type-listbox">
            {APPLICATION_TYPE_OPTIONS.map((o) => (
              <li
                key={o.value}
                role="option"
                aria-selected={applicationType === o.value}
                tabIndex={0}
                className={`fields-page__type-option${applicationType === o.value ? " fields-page__type-option--selected" : ""}`}
                onClick={() => {
                  setApplicationType(o.value);
                  setTypeOpen(false);
                  typeTriggerRef.current?.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setApplicationType(o.value);
                    setTypeOpen(false);
                    typeTriggerRef.current?.focus();
                  }
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>
        )}
      </span>

      <span className="fields-page__field">
        <label htmlFor={productId}>Product</label>
        <input
          id={productId}
          type="text"
          maxLength={120}
          value={product}
          onChange={(e) => setProduct(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={rateId}>Rate (optional)</label>
        <input
          id={rateId}
          type="number"
          min="0.01"
          step="0.01"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={unitId}>Unit (optional)</label>
        <input
          id={unitId}
          type="text"
          maxLength={20}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={appliedOnId}>Applied on</label>
        <input
          id={appliedOnId}
          type="date"
          value={appliedOn}
          onChange={(e) => setAppliedOn(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={costId}>Cost (optional)</label>
        <input
          id={costId}
          type="number"
          min="0.01"
          step="0.01"
          className="fields-page__money-input"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
        />
      </span>

      {error && (
        <p className="fields-page__field-error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="btn btn--primary fields-page__btn" disabled={submitting}>
        {submitting ? "…" : "Log application"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------------
// ef-045-5 — harvest, yield per acre, and the crop sale. APPENDED below slice 4's own
// `ApplicationForm`; nothing above this line changes. Calls `FieldHarvestEndpoints.cs`/
// `FieldHarvestContracts.cs` via `api/client.ts`'s own `// ---- ef-045-5: harvest ----`
// section. NO SEPARATE "SELL" STEP — this form's own POST both records the harvest and
// posts its own crop sale (`source = 'crop'`) in the same write; there is no second
// action, mirroring D5's "reuse the pre-existing two-line shim exactly as
// `source='livestock'` already does" — see `FieldCropApplications`'s own remarks one
// section up for where this form is mounted.
// ---------------------------------------------------------------------------------

/**
 * The per-field-crop harvest form — one `Record harvest` action, no edit/remove (no
 * PATCH/DELETE route exists this wave, same posture `ApplicationForm`'s own header takes
 * for applications). Mirrors `ApplicationForm`'s own shape one section up: a client-side
 * check for the exact cases `fields-harvest.spec.ts`'s own AC3 exercises (a non-positive
 * quantity), leaving every other rule — including the MISSING-4010-ACCOUNT 409, which
 * this shared E2E tenant can never reach — to the real contract in
 * `FieldHarvestEndpoints.cs`.
 *
 * ef-046-1c — ALSO owns the "Store in bin" picker: a plain `<select>` (unlike
 * `ApplicationForm`'s own hand-rolled combobox one section up — a handful of bin names is
 * never long enough to need one) defaulting to "Sell now", listing bins for this entity.
 * Picking a real bin makes `pricePerUnit` optional client-side and sends `binId` instead
 * (SOLD XOR STORED — `CreateFieldHarvestRequest`'s own remarks in
 * `FieldHarvestContracts.cs`); leaving it on "Sell now" reproduces ef-045-5's own
 * byte-identical sold path, `binId` omitted entirely from the request body.
 */
function HarvestForm({
  fieldCropId,
  entityId,
  crop,
  onRecorded,
}: {
  fieldCropId: Guid;
  entityId: Guid | null;
  /** ef-057-1a — the field-crop's crop; the contract picker lists open contracts for it. */
  crop: string;
  onRecorded: () => void;
}) {
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState<FieldHarvestUnit>("bu");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [harvestedOn, setHarvestedOn] = useState("");
  const [binId, setBinId] = useState("");
  const [bins, setBins] = useState<GrainBin[]>([]);
  const [contractId, setContractId] = useState("");
  const [paid, setPaid] = useState(false);
  const [contracts, setContracts] = useState<GrainContract[]>([]);
  const [contractsState, setContractsState] = useState<"loading" | "ready" | "error">("loading");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qtyId = useId();
  const unitId = useId();
  const priceId = useId();
  const harvestedOnId = useId();
  const binIdId = useId();
  const contractIdId = useId();
  const paidId = useId();

  /* ef-046-1c — bins for this entity, fetched once entityId resolves. A load failure
     here degrades to "Sell now only" (the picker still renders with just its default
     option) rather than blocking the whole form — bin storage is additive to the
     pre-existing sale path, never a precondition for it. */
  useEffect(() => {
    if (entityId === null) return;
    let cancelled = false;
    void demoApi
      .listGrainBins(entityId)
      .then((list) => {
        if (!cancelled) setBins(list);
      })
      .catch(() => {
        /* degrade to "Sell now only" — see remarks above */
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  /* ef-057-1a — contracts for this entity, fetched once entityId resolves. A load
     failure degrades to "No contract only" (same posture as the bin picker above). The
     crop/status filter is derived at render, never stored. */
  useEffect(() => {
    if (entityId === null) return;
    let cancelled = false;
    setContractsState("loading");
    void demoApi
      .getGrainContracts(entityId)
      .then((list) => {
        if (cancelled) return;
        setContracts(list);
        setContractsState("ready");
      })
      .catch(() => {
        if (!cancelled) setContractsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  const wantedCrop = crop.trim().toLowerCase();
  const openContracts = contracts.filter(
    (c) => c.status !== "fulfilled" && c.crop.trim().toLowerCase() === wantedCrop,
  );

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    /* @ui-check: client-side ONLY — FieldHarvestEndpoints.cs's own ValidateQty/
       ValidateUnit/ValidatePricePerUnit and the required harvestedOn check still run
       server-side and remain the actual contract. Same "surface the reason immediately"
       posture ApplicationForm's own submit takes above. */
    const qtyValue = Number(qty);
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }
    const storing = binId !== "";
    const delivering = contractId !== "";
    let priceValue: number | undefined;
    if (delivering) {
      /* price is optional on a contract delivery — the server uses the contract's own */
      if (pricePerUnit !== "") {
        priceValue = Number(pricePerUnit);
        if (!Number.isFinite(priceValue) || priceValue <= 0) {
          setError("Price per unit must be greater than zero.");
          return;
        }
      }
    } else if (!storing) {
      priceValue = Number(pricePerUnit);
      if (!Number.isFinite(priceValue) || priceValue <= 0) {
        setError("Price per unit must be greater than zero.");
        return;
      }
    }
    if (harvestedOn === "") {
      setError("Harvested on is required.");
      return;
    }

    setSubmitting(true);
    try {
      await demoApi.postFieldHarvest(fieldCropId, {
        harvestedOn,
        qty: qtyValue,
        unit,
        ...(delivering
          ? {
              deliverTo: { contractId, paid },
              ...(priceValue !== undefined ? { pricePerUnit: priceValue } : {}),
            }
          : storing
            ? { binId }
            : { pricePerUnit: priceValue }),
      });
      setQty("");
      setPricePerUnit("");
      setHarvestedOn("");
      setBinId("");
      setContractId("");
      setPaid(false);
      onRecorded();
    } catch (err) {
      setError(problemMessage(err, "Could not record this harvest."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="fields-page__harvest-form" onSubmit={(e) => void submit(e)} noValidate>
      <span className="fields-page__field">
        <label htmlFor={qtyId}>Quantity</label>
        <input
          id={qtyId}
          type="number"
          min="0.01"
          step="0.01"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={unitId}>Unit</label>
        <select id={unitId} value={unit} onChange={(e) => setUnit(e.target.value as FieldHarvestUnit)}>
          <option value="bu">Bushels</option>
          <option value="ton">Tons</option>
          <option value="cwt">CWT</option>
        </select>
      </span>

      <span className="fields-page__field">
        <label htmlFor={binIdId}>Store in bin</label>
        <select
          id={binIdId}
          value={binId}
          disabled={contractId !== ""}
          onChange={(e) => setBinId(e.target.value)}
        >
          <option value="">Sell now</option>
          {bins.map((bin) => (
            <option key={bin.id} value={bin.id}>
              {grainBinLabel(bin)}
            </option>
          ))}
        </select>
      </span>

      {/* ef-057-1a — "Deliver to contract": a harvest straight to an open grain contract
          for this crop, bypassing every bin. Mutually exclusive with "Store in bin".
          @ui-check: the picker is DISABLED while data-state="loading" — an enabled-but-empty
          select there reads as "no open contracts" and loses the pick to the in-flight fetch.
          @ui-check: a contract-less submit is byte-identical to the old (sold/stored) path —
          no `deliverTo` key, no `paid`. Units are NOT filtered: the server's 400 explains a
          mismatch. */}
      <span className="fields-page__field">
        <label htmlFor={contractIdId}>Deliver to contract</label>
        <select
          id={contractIdId}
          value={contractId}
          disabled={binId !== "" || contractsState === "loading"}
          data-state={contractsState}
          aria-busy={contractsState === "loading"}
          onChange={(e) => {
            setContractId(e.target.value);
            if (e.target.value === "") setPaid(false);
          }}
        >
          <option value="">No contract</option>
          {openContracts.map((c) => (
            <option key={c.id} value={c.id}>
              {`${c.crop}, ${c.remaining} ${c.unit} remaining at $${c.pricePerUnit.toFixed(2)}`}
            </option>
          ))}
        </select>
      </span>

      {contractId !== "" && (
        <span className="fields-page__field">
          <input id={paidId} type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
          <label htmlFor={paidId}>Paid now</label>
        </span>
      )}

      <span className="fields-page__field">
        <label htmlFor={priceId}>Price per unit</label>
        <input
          id={priceId}
          type="number"
          min="0.01"
          step="0.01"
          className="fields-page__money-input"
          value={pricePerUnit}
          disabled={binId !== ""}
          onChange={(e) => setPricePerUnit(e.target.value)}
        />
      </span>

      <span className="fields-page__field">
        <label htmlFor={harvestedOnId}>Harvested on</label>
        <input
          id={harvestedOnId}
          type="date"
          value={harvestedOn}
          onChange={(e) => setHarvestedOn(e.target.value)}
        />
      </span>

      {error && (
        <p className="fields-page__field-error" role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="btn btn--primary fields-page__btn" disabled={submitting}>
        {submitting ? "…" : "Record harvest"}
      </button>
    </form>
  );
}
